import {
  AMRAP_EFFORT_BIN_INDEX,
  assignRepRangeBucketId,
  createEmptyMuscleCountMaps,
  createEmptyRepRangeCounts,
  EFFORT_BIN_COUNT,
  effortBucketsFromBinCounts,
  effortStepToIndexForMeasure,
  INSIGHTS_MUSCLE_CHART_ROW_COUNT,
  muscleRowsFromCountMaps,
  parseRirNumericForHistogram,
  parseRpeNumericForHistogram,
  repRangeBucketsFromCounts,
  resolveMuscleChartIds,
} from "./insightsDistributions";
import {
  exerciseCountsTowardLensCompVolume,
  INSIGHTS_PER_LIFT_LENS_IDS,
  mapExerciseToLensLiftId,
} from "./insightsLensCompVolume";
import {
  accumulateLiftSetRowIntoScalars,
  createEmptyLiftScalars,
} from "./insightsPerLiftScalars";
import {
  accumulatePrescribedAvgRpeRow,
  createEmptyPrescribedRpeAccumulator,
  finalizeAvgPrescribedRpe,
} from "./insightsPrescribedRpe";
import {
  countedRepsForSetRow,
  forEachInsightsSetRow,
  INSIGHTS_DATA_SOURCE,
  isAmrapSetRow,
  parsePositiveNumber,
  parseRepsForSet,
} from "./insightsSetRowMath";
import { rpeMidpoint } from "../../../../../utils/rpe-range";

/** Function to create per lift scalar maps by lens.
 * @param {"rpe"|"rir"} intensityMeasure
 */
function createPerLiftScalarMapsByLens() {
  /** @type {Record<string, Map<string, ReturnType<typeof createEmptyLiftScalars>>>} */
  const perLiftScalarsByLens = {};
  /** @type {Record<string, Set<string>>} */
  const perLiftProgrammedLiftIdsByLens = {};
  for (const lensId of INSIGHTS_PER_LIFT_LENS_IDS) {
    perLiftScalarsByLens[lensId] = new Map();
    perLiftProgrammedLiftIdsByLens[lensId] = new Set();
  }
  return { perLiftScalarsByLens, perLiftProgrammedLiftIdsByLens };
}

/**
 * @param {Record<string, Map<string, ReturnType<typeof createEmptyLiftScalars>>>} perLiftScalarsByLens
 * @param {string} lensId
 * @param {string} liftId
 * @returns {ReturnType<typeof createEmptyLiftScalars>}
 */
function getOrCreateLiftScalars(perLiftScalarsByLens, lensId, liftId) {
  const byLift = perLiftScalarsByLens[lensId];
  let scalars = byLift.get(liftId);
  if (!scalars) {
    scalars = createEmptyLiftScalars();
    byLift.set(liftId, scalars);
  }
  return scalars;
}

/**
 * @param {object} row
 * @param {number} mult
 * @param {{
 *   pIds: string[],
 *   sIds: string[],
 *   primary: Map<string, number>,
 *   secondary: Map<string, number>,
 *   repRangeCounts: object,
 *   intensityMeasure: "rpe"|"rir",
 *   effortStepToIndex: Map<number, number>,
 *   effortBinCounts: number[],
 *   amrapSetCounter: { count: number },
 * }} ctx
 */
function accumulateSetRowDistributions(row, mult, ctx) {
  const {
    pIds,
    sIds,
    primary,
    secondary,
    repRangeCounts,
    intensityMeasure,
    effortStepToIndex,
    effortBinCounts,
    amrapSetCounter,
    dataSource,
  } = ctx;

  const pIdSet = new Set(pIds);
  for (const pId of pIds) {
    primary.set(pId, (primary.get(pId) ?? 0) + mult);
  }
  for (const sId of sIds) {
    if (!pIdSet.has(sId)) {
      secondary.set(sId, (secondary.get(sId) ?? 0) + mult);
    }
  }

  if (isAmrapSetRow(row)) {
    if (dataSource === INSIGHTS_DATA_SOURCE.OUTCOMES) {
      // Actual reps are known — classify into the numeric bucket they fell into.
      // Fall back to amrap bucket only if no reps were logged.
      const bucketId = assignRepRangeBucketId(parseRepsForSet(row));
      repRangeCounts[bucketId ?? "amrap"] += mult;
      if (!bucketId) {
        amrapSetCounter.count += mult;
      }
    } else {
      // Prescribed (not yet performed) — reps are unknowable, so count it as
      // an AMRAP bar on the RPE/RiR distribution chart instead of Rep Range.
      effortBinCounts[AMRAP_EFFORT_BIN_INDEX] += mult;
      amrapSetCounter.count += mult;
    }
    return;
  }
  const bucketId = assignRepRangeBucketId(parseRepsForSet(row));
  if (bucketId) {
    repRangeCounts[bucketId] += mult;
  }

  const effortValue =
    intensityMeasure === "rir"
      ? parseRirNumericForHistogram(rpeMidpoint(row))
      : parseRpeNumericForHistogram(rpeMidpoint(row));
  if (effortValue == null) {
    return;
  }
  const idx = effortStepToIndex.get(effortValue);
  if (idx != null) {
    effortBinCounts[idx] += mult;
  }
}

/** Function to accumulate set row per lift.
 * @param {object} row
 * @param {number} mult
 * @param {Record<string, string|null>} liftIdsByLens
 * @param {Record<string, Map<string, ReturnType<typeof createEmptyLiftScalars>>>} perLiftScalarsByLens
 * @param {Record<string, Set<string>>} perLiftProgrammedLiftIdsByLens
 */
function accumulateSetRowPerLift(
  row,
  mult,
  exercise,
  liftIdsByLens,
  perLiftScalarsByLens,
  perLiftProgrammedLiftIdsByLens,
  dataSource
) {
  for (const lensId of INSIGHTS_PER_LIFT_LENS_IDS) {
    const liftId = liftIdsByLens[lensId];
    if (!liftId) {
      continue;
    }
    accumulateLiftSetRowIntoScalars(
      row,
      mult,
      getOrCreateLiftScalars(perLiftScalarsByLens, lensId, liftId),
      exercise,
      dataSource
    );
    perLiftProgrammedLiftIdsByLens[lensId].add(liftId);
  }
}

/**
 * Single pass over a week's exercises: overview, distributions, per-lift scalars, lens coverage inputs.
 *
 * @param {object[]} exercises — `collectResolvedExercisesForWeek` output.
 * @param {{ intensityMeasure?: "rpe"|"rir", dataSource?: string }} [options]
 */
export function scanWeekExercises(
  exercises,
  {
    intensityMeasure = "rpe",
    dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED,
  } = {}
) {
  const exerciseIds = new Set();
  let totalSets = 0;
  let estimatedReps = 0;
  let volumeKg = 0;
  const lensCompVolumeKg = {
    powerlifting: 0,
    olympic_weightlifting: 0,
    strongman: 0,
    streetlifting: 0,
  };

  const { primary, secondary } = createEmptyMuscleCountMaps();
  const repRangeCounts = createEmptyRepRangeCounts();
  const effortBinCounts = new Array(EFFORT_BIN_COUNT).fill(0);
  const effortStepToIndex = effortStepToIndexForMeasure(intensityMeasure);
  /** Week total of AMRAP sets, independent of which chart displays them. */
  const amrapSetCounter = { count: 0 };

  const { perLiftScalarsByLens, perLiftProgrammedLiftIdsByLens } =
    createPerLiftScalarMapsByLens();
  const prescribedRpeAcc = createEmptyPrescribedRpeAccumulator();

  for (const ex of exercises) {
    let exerciseVolumeKg = 0;
    let insightRowCount = 0;

    const distributionCtx = {
      primary,
      secondary,
      repRangeCounts,
      intensityMeasure,
      effortStepToIndex,
      effortBinCounts,
      amrapSetCounter,
      dataSource,
    };

    forEachInsightsSetRow(
      ex,
      (row, mult, rowExercise) => {
        insightRowCount += mult;
        const metaEx = rowExercise ?? ex;
        const rowPIds = resolveMuscleChartIds(metaEx?.primaryMuscleGroup);
        const rowSIds = resolveMuscleChartIds(metaEx?.secondaryMuscleGroup);
        const rowLiftIdsByLens = Object.fromEntries(
          INSIGHTS_PER_LIFT_LENS_IDS.map((lensId) => [
            lensId,
            mapExerciseToLensLiftId(metaEx, lensId),
          ])
        );

        totalSets += mult;

        const reps = countedRepsForSetRow(row, metaEx, dataSource);
        estimatedReps += mult * reps;

        const loadKg = parsePositiveNumber(row?.load);
        if (loadKg > 0 && reps > 0) {
          exerciseVolumeKg += mult * reps * loadKg;
        }

        accumulateSetRowDistributions(row, mult, {
          pIds: rowPIds,
          sIds: rowSIds,
          ...distributionCtx,
        });
        accumulatePrescribedAvgRpeRow(row, mult, metaEx, prescribedRpeAcc);
        accumulateSetRowPerLift(
          row,
          mult,
          metaEx,
          rowLiftIdsByLens,
          perLiftScalarsByLens,
          perLiftProgrammedLiftIdsByLens,
          dataSource
        );
      },
      dataSource
    );

    /** If the data source is outcomes, only count the exercise if it has at least one row. */
    const countsTowardExerciseTotal =
      dataSource === INSIGHTS_DATA_SOURCE.OUTCOMES ? insightRowCount > 0 : true;
    if (ex?._id != null && countsTowardExerciseTotal) {
      exerciseIds.add(String(ex._id));
    }

    volumeKg += exerciseVolumeKg;
    if (exerciseVolumeKg > 0) {
      for (const lensId of INSIGHTS_PER_LIFT_LENS_IDS) {
        if (exerciseCountsTowardLensCompVolume(ex, lensId)) {
          lensCompVolumeKg[lensId] += exerciseVolumeKg;
        }
      }
    }
  }

  const muscles = muscleRowsFromCountMaps(primary, secondary);
  const muscleChartDistinctWithVolume = muscles.filter(
    (row) => row.primary + row.secondary > 0
  ).length;

  return {
    exerciseCount: exerciseIds.size,
    totalSets,
    estimatedReps: Math.round(estimatedReps),
    volumeKg,
    avgPrescribedRpe: finalizeAvgPrescribedRpe(prescribedRpeAcc),
    sbdVolumeKg: lensCompVolumeKg.powerlifting,
    lensCompVolumeKg,
    muscleGroupsPair: [
      muscleChartDistinctWithVolume,
      INSIGHTS_MUSCLE_CHART_ROW_COUNT,
    ],
    muscleGroupsValue: muscleChartDistinctWithVolume,
    muscles,
    repRanges: repRangeBucketsFromCounts(
      repRangeCounts,
      dataSource === INSIGHTS_DATA_SOURCE.OUTCOMES
    ),
    rpeDistribution: effortBucketsFromBinCounts(
      effortBinCounts,
      intensityMeasure
    ),
    amrapSets: amrapSetCounter.count,
    perLiftScalarsByLens,
    perLiftProgrammedLiftIdsByLens,
  };
}
